const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 дней

const STORAGE_KEYS = {
  geocode: 'ymapsAddressCache:geocode:v1',
  suggest: 'ymapsAddressCache:suggest:v1'
}

// options может прийти от ymaps.control.SearchControl - там, помимо boundedBy/results/strictBounds,
// лежит ссылка на сам provider (циклическая, т.к. это наш же объект), поэтому нельзя просто
// JSON.stringify(options) целиком - берём только примитивные поля, нужные для ключа кэша.
const normalizeKey = (query, options) => {
  const normalizedQuery = (query || '').trim().toLowerCase().replace(/\s+/g, ' ')
  const safeOptions = {
    boundedBy: options && options.boundedBy,
    results: options && options.results,
    strictBounds: options && options.strictBounds,
    json: options && options.json
  }
  return `${normalizedQuery}|${JSON.stringify(safeOptions)}`
}

// localStorage может быть недоступен (приватный режим, переполнение) - все обращения обёрнуты
// в try/catch, в худшем случае кэш просто не переживёт перезагрузку страницы.
const loadCache = (storageKey) => {
  const map = new Map()
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return map
    const parsed = JSON.parse(raw)
    const now = Date.now()
    Object.keys(parsed).forEach(key => {
      const entry = parsed[key]
      if (entry && typeof entry.timestamp === 'number' && (now - entry.timestamp) < CACHE_TTL_MS) {
        map.set(key, entry)
      }
    })
  } catch (e) {}
  return map
}

const persistCache = (storageKey, map) => {
  try {
    const obj = {}
    map.forEach((entry, key) => {
      obj[key] = entry
    })
    localStorage.setItem(storageKey, JSON.stringify(obj))
  } catch (e) {}
}

const geocodeCache = loadCache(STORAGE_KEYS.geocode)
const suggestCache = loadCache(STORAGE_KEYS.suggest)

const getCached = (map, storageKey, key) => {
  const entry = map.get(key)
  if (!entry) return undefined
  if (Date.now() - entry.timestamp >= CACHE_TTL_MS) {
    map.delete(key)
    persistCache(storageKey, map)
    return undefined
  }
  return entry.value
}

const setCached = (map, storageKey, key, value) => {
  map.set(key, {value, timestamp: Date.now()})
  persistCache(storageKey, map)
}

const GEOCODE_HTTP_URL = 'https://geocode-maps.yandex.ru/1.x/'

/**
 * Геокодирование с persistent-кэшем (localStorage, 7 дней).
 * Ходит НЕ через window.ymaps.geocode, а напрямую в HTTP Геокодер со своим ключом
 * (options.apikey). Причина: ymaps.geocode всегда подставляет ключ JS API, а у нас ключи
 * JS API и Геокодера - раздельные продукты, и на ключе JS API геокодер отвечает 403.
 * Формат ответа (response.GeoObjectCollection) совпадает с ymaps.geocode({json: true}).
 * @param {string} address - Адрес для геокодирования
 * @param {Object} options - {apikey, results, boundedBy, strictBounds}
 * @returns {Promise<Object>} JSON-ответ геокодера ({GeoObjectCollection: ...})
 */
export function cachedGeocode (address, options) {
  const key = normalizeKey(address, options)
  const cached = getCached(geocodeCache, STORAGE_KEYS.geocode, key)
  if (cached !== undefined) { return Promise.resolve(cached) }

  const params = [
    'apikey=' + encodeURIComponent(options.apikey || ''),
    'geocode=' + encodeURIComponent(address),
    'format=json',
    'results=' + (options.results || 10)
  ]
  if (options.boundedBy) {
    // boundedBy приходит как [[lat, lon], [lat, lon]] (в проекте coordorder=latlong),
    // а HTTP Геокодер ждёт bbox в порядке долгота,широта
    const [p1, p2] = options.boundedBy
    params.push('bbox=' + p1[1] + ',' + p1[0] + '~' + p2[1] + ',' + p2[0])
    if (options.strictBounds) { params.push('rspn=1') }
  }

  return fetch(GEOCODE_HTTP_URL + '?' + params.join('&')).then(res => {
    if (!res.ok) throw new Error('HTTP Геокодер ответил ' + res.status)
    return res.json()
  }).then(data => {
    const result = data.response
    setCached(geocodeCache, STORAGE_KEYS.geocode, key, result)
    return result
  })
}

/**
 * Локальный поиск подсказок по подстроке среди всех закэшированных suggest-ответов.
 * Используется как фоллбек, когда сетевой саджест недоступен (исчерпан дневной
 * лимит запросов / нет сети) - лучше показать неполный список из кэша, чем ничего.
 * @param {string} query - Введённый текст
 * @param {number} limit - Максимум подсказок
 * @returns {Array} Массив подсказок ({value, displayName})
 */
export function suggestFromCacheBySubstring (query, limit) {
  const normalizedQuery = (query || '').trim().toLowerCase().replace(/\s+/g, ' ')
  if (!normalizedQuery) return []

  const seen = new Set()
  const matches = []
  suggestCache.forEach(entry => {
    (entry.value || []).forEach(item => {
      if (!item || !item.value || seen.has(item.value)) return
      if (item.value.toLowerCase().indexOf(normalizedQuery) !== -1) {
        seen.add(item.value)
        matches.push(item)
      }
    })
  })
  return matches.slice(0, limit || 5)
}

/**
 * Поисковые подсказки с persistent-кэшем (localStorage, 7 дней).
 * @param {string} query - Текст запроса
 * @param {Object} options - {boundedBy, results, strictBounds}
 * @returns {Promise<Array>} Массив подсказок ({value, displayName})
 */
export function cachedSuggest (query, options) {
  const key = normalizeKey(query, options)
  const cached = getCached(suggestCache, STORAGE_KEYS.suggest, key)
  if (cached !== undefined) {
    // SearchControl ждёт от provider.suggest() именно ymaps.vow.Promise
    // (с методами .done()/.fail()), а не обычный нативный Promise - иначе падает
    // с "t.done is not a function" сразу после получения результата из кэша.
    return window.ymaps.vow.resolve(cached)
  }

  return window.ymaps.suggest(query, options).then(result => {
    setCached(suggestCache, STORAGE_KEYS.suggest, key, result)
    return result
  })
}



// WEBPACK FOOTER //
// ./src/helpers/yandexAddressCache.js